import React, { useMemo } from 'react';
import { Select } from 'antd';
import { ActivityFaIcon, ActivityTypeLabel } from './FollowUpActivitySelect';
import { normalizeFollowUpActivities } from '../../utils/followUpActivityUtils';

export const FOLLOW_UP_QUICK_ACTIVITY_DEFS = [
  { key: 'incoming', label: 'تماس ورودی', keywords: ['تماس ورودی', 'تلفنی ورودی', 'incoming', 'phone-volume', 'fa-phone-volume'] },
  { key: 'outgoing', label: 'تماس خروجی', keywords: ['تماس خروجی', 'تلفنی خروجی', 'outgoing', 'fa-phone'] },
  { key: 'no_answer', label: 'تماس بی‌پاسخ', keywords: ['تماس بی پاسخ', 'تماس بی‌پاسخ', 'بی پاسخ', 'بی‌پاسخ', 'phone-slash', 'fa-phone-slash'] },
  { key: 'message', label: 'پیام', keywords: ['پیامک', 'sms', 'fa-sms'] },
];

const resolveActivity = (activityMap, id) =>
  activityMap.get(id) ??
  activityMap.get(Number(id)) ??
  activityMap.get(String(id));

const normalizeText = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

export const findActivityByKeywords = (activities, keywords = [], preferredLabel = '') => {
  if (!activities?.length) return null;

  const preferred = normalizeText(preferredLabel);
  if (preferred) {
    const exact = activities.find((activity) => normalizeText(activity.name) === preferred);
    if (exact) return exact;
  }

  if (!keywords.length) return null;

  const normalizedKeywords = keywords
    .map(normalizeText)
    .filter((kw) => kw.length >= 2)
    .sort((a, b) => b.length - a.length);

  for (const kw of normalizedKeywords) {
    const match = activities.find((activity) => {
      const haystack = normalizeText(`${activity.name || ''} ${activity.icon || ''}`);
      return haystack.includes(kw);
    });
    if (match) return match;
  }

  return null;
};

/**
 * انتخاب نوع فعالیت پیگیری — دکمه‌های پرکاربرد + Select سایر موارد
 */
const FollowUpActivityPicker = ({
  activities = [],
  loading,
  value,
  onChange,
  disabled,
  onAfterChange,
  showOtherActivities = true,
}) => {
  const normalizedActivities = useMemo(
    () => normalizeFollowUpActivities(activities),
    [activities],
  );

  const activityMap = useMemo(() => {
    const map = new Map();
    normalizedActivities.forEach((activity) => {
      map.set(activity.id, activity);
      map.set(Number(activity.id), activity);
      map.set(String(activity.id), activity);
    });
    return map;
  }, [normalizedActivities]);

  const quickActivities = useMemo(
    () =>
      FOLLOW_UP_QUICK_ACTIVITY_DEFS.map((def) => ({
        ...def,
        activity: findActivityByKeywords(normalizedActivities, def.keywords, def.label),
      })),
    [normalizedActivities],
  );

  const quickIds = useMemo(
    () => new Set(quickActivities.map((q) => q.activity?.id).filter(Boolean)),
    [quickActivities],
  );

  const otherActivities = useMemo(
    () => normalizedActivities.filter((a) => !quickIds.has(a.id)),
    [normalizedActivities, quickIds],
  );

  const options = useMemo(
    () =>
      otherActivities.map((activity) => ({
        value: activity.id,
        label: activity.name,
        title: activity.name,
      })),
    [otherActivities],
  );

  const selectedInQuick = quickIds.has(value);

  const emitChange = (activityId) => {
    onChange?.(activityId);
    onAfterChange?.(activityId);
  };

  return (
    <div className="space-y-3 follow-up-activity-picker">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {quickActivities.map((item) => {
          const isActive = item.activity && value === item.activity.id;
          return (
            <button
              key={item.key}
              type="button"
              disabled={disabled || loading || !item.activity}
              onClick={() => item.activity && emitChange(item.activity.id)}
              className={[
                'follow-up-quick-activity-btn flex flex-col items-center justify-center gap-1.5 p-2.5 rounded-xl border text-center transition-all duration-200 min-h-[72px]',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/40',
                !item.activity ? 'opacity-40 cursor-not-allowed border-gray-200 dark:border-gray-700' : '',
                isActive
                  ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 shadow-sm ring-1 ring-teal-500/30'
                  : item.activity
                    ? 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-teal-400 hover:bg-teal-50/50 dark:hover:bg-teal-900/10'
                    : '',
              ]
                .filter(Boolean)
                .join(' ')}
              style={{ fontFamily: 'CustomFont' }}
            >
              {item.activity ? (
                <ActivityFaIcon
                  icon={item.activity.icon}
                  color={item.activity.color || '#666'}
                  size={20}
                />
              ) : (
                <span className="w-4 h-4 rounded-full bg-gray-300" />
              )}
              <span className="text-[11px] font-medium text-gray-700 dark:text-gray-200 leading-tight">
                {item.label}
              </span>
            </button>
          );
        })}
      </div>

      {showOtherActivities && otherActivities.length > 0 && (
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5 custom-font">سایر فعالیت‌ها</p>
          <Select
            placeholder="انتخاب نوع فعالیت دیگر"
            loading={loading}
            disabled={disabled}
            value={selectedInQuick ? undefined : value}
            onChange={emitChange}
            style={{ fontFamily: 'CustomFont', width: '100%' }}
            size="large"
            showSearch
            allowClear
            virtual={false}
            options={options}
            popupClassName="follow-up-activity-select-dropdown"
            filterOption={(input, option) =>
              String(option?.label ?? '')
                .toLowerCase()
                .includes(input.trim().toLowerCase())
            }
            optionRender={(option) => {
              const activity = resolveActivity(activityMap, option.value);
              return <ActivityTypeLabel activity={activity} />;
            }}
            labelRender={({ value: selectedValue }) => {
              const activity = resolveActivity(activityMap, selectedValue);
              if (activity) return <ActivityTypeLabel activity={activity} />;
              return <span style={{ fontFamily: 'CustomFont' }}>{selectedValue}</span>;
            }}
          />
        </div>
      )}

      {value && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700">
          <span className="text-xs text-gray-500 custom-font">انتخاب‌شده:</span>
          <ActivityTypeLabel activity={resolveActivity(activityMap, value)} className="text-sm" />
        </div>
      )}
    </div>
  );
};

export default FollowUpActivityPicker;
